import {
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { gsap } from 'gsap';

export interface LiquidTransitionHandle {
  play: (onCovered: () => void, onComplete?: () => void) => void;
  kill: () => void;
}

export const LiquidTransition = forwardRef<LiquidTransitionHandle>(
  function LiquidTransition(_, forwardedRef) {
    const rootRef = useRef<HTMLDivElement>(null);
    const firstLayerRef = useRef<HTMLDivElement>(null);
    const secondLayerRef = useRef<HTMLDivElement>(null);
    const shineRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<gsap.core.Timeline | null>(null);

    useImperativeHandle(forwardedRef, () => ({
      play(onCovered, onComplete) {
        const root = rootRef.current;
        const firstLayer = firstLayerRef.current;
        const secondLayer = secondLayerRef.current;
        const shine = shineRef.current;

        if (!root || !firstLayer || !secondLayer || !shine) {
          onCovered();
          onComplete?.();
          return;
        }

        timelineRef.current?.kill();
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reducedMotion) {
          timelineRef.current = gsap
            .timeline({ onComplete: onComplete ?? (() => undefined) })
            .set(root, { autoAlpha: 1, pointerEvents: 'auto' })
            .to(root, { opacity: 1, duration: 0.12 })
            .call(onCovered)
            .to(root, { opacity: 0, duration: 0.18, delay: 0.05 })
            .set(root, { visibility: 'hidden', pointerEvents: 'none' });
          return;
        }

        timelineRef.current = gsap
          .timeline({
            defaults: { ease: 'power3.inOut' },
            onComplete: onComplete ?? (() => undefined),
          })
          .set(root, { autoAlpha: 1, pointerEvents: 'auto' })
          .set(firstLayer, { xPercent: -85, yPercent: 42, rotation: -13 })
          .set(secondLayer, { xPercent: -95, yPercent: 48, rotation: 9 })
          .set(shine, { opacity: 0 })
          .to(firstLayer, { xPercent: -5, yPercent: -5, rotation: -4, duration: 0.46 }, 0)
          .to(secondLayer, { xPercent: -8, yPercent: -1, rotation: 4, duration: 0.48 }, 0.02)
          .to(shine, { opacity: 1, duration: 0.12 }, 0.34)
          .call(onCovered, [], 0.43)
          .to(shine, { opacity: 0, duration: 0.2 }, 0.48)
          .to(firstLayer, { xPercent: 95, yPercent: -50, rotation: 8, duration: 0.48 }, 0.5)
          .to(secondLayer, { xPercent: 105, yPercent: -42, rotation: -8, duration: 0.5 }, 0.5)
          .set(root, { autoAlpha: 0, pointerEvents: 'none' });
      },
      kill() {
        timelineRef.current?.kill();
      },
    }));

    return (
      <div ref={rootRef} className="liquid-transition" aria-hidden="true">
        <div
          ref={firstLayerRef}
          className="liquid-transition__layer liquid-transition__layer--one"
        />
        <div
          ref={secondLayerRef}
          className="liquid-transition__layer liquid-transition__layer--two"
        />
        <div ref={shineRef} className="liquid-transition__shine" />
      </div>
    );
  },
);
